import timeTemplate from './time.html';
import timeInlineTemplate from './time-inline.html';

import angular from 'angular';

const INLINE_CONTAINER_SELECTOR = 'li.akam-time-inline-container';
const INLINE_CLASS = 'akam-time-inline';

class TimeController {
  static get $inject() {
    return ['$log', '$translate', '$attrs', 'hours', 'militaryHours', '$timeout'];
  }

  constructor($log, $translate, $attrs, hours, militaryHours, $timeout) {
    this.$log = $log;
    this.$translate = $translate;
    this.$attrs = $attrs;
    this.hours = hours;
    this.militaryHours = militaryHours;
    this.$timeout = $timeout;
  }

  $onInit() {
    this.hours = angular.isDefined(this.$attrs.noMeridian) ? this.militaryHours : this.hours;
    this.inline = angular.isDefined(this.$attrs.inline);

    if (this.placeholder == null) {
      this.placeholder = this.hours.placeholder;
    } else {
      this.$translate(this.placeholder)
        .then(placeholder => this.placeholder = placeholder)
        .catch(placeholder => this.placeholder = placeholder);
    }

    this.hourStep = this.hourStep || 1;
    this.minuteStep = this.minuteStep || 15;

    this.ngModel.$validators.minTime = (modelValue, viewValue) => {
      if (!modelValue && !viewValue) {
        return true;
      }
      const value = modelValue || this.parseView(viewValue);
      return !this.isLessThanMinTime(value);
    };

    this.ngModel.$validators.maxTime = (modelValue, viewValue) => {
      if (!modelValue && !viewValue) {
        return true;
      }
      const value = modelValue || this.parseView(viewValue);
      return !this.isGreaterThanMaxTime(value);
    };

    this.ngModel.$formatters.push(this.formatModel.bind(this));
    this.ngModel.$render = this.renderView.bind(this);
    this.ngModel.$parsers.push(this.parseView.bind(this));
  }

  $onChanges(changes) {
    const minTime = changes.minTime && changes.minTime.currentValue;
    const maxTime = changes.maxTime && changes.maxTime.currentValue;
    this.setValidLimits(minTime, maxTime);

    // this checking is intended to avoid this.updateTime() when ngModel(this.time) is undefined
    if (!this.time) {
      return;
    }

    const parsedTime = this.parseView(this.time);
    this.time = this.getValidTime(parsedTime);
    this.updateTime();
  }

  isLessThanMinTime(timeValue) {
    return this.minTime && this.inMinutes(timeValue) < this.inMinutes(this.minTime);
  }

  isGreaterThanMaxTime(timeValue) {
    return this.maxTime && this.inMinutes(timeValue) > this.inMinutes(this.maxTime);
  }

  getViewValue(time) {
    const viewValue = this.hours.viewHours(time.hours || 0);
    viewValue.hours = this.formatNumber(viewValue.hours);
    viewValue.minutes = this.formatNumber(time.minutes || 0);

    return viewValue;
  }

  getValidLimit(limit) {
    const dayInMinutes = 24 * 60;
    const limitInMinutes = this.inMinutes(limit);

    if (limitInMinutes < 0 || limitInMinutes > dayInMinutes) {
      return undefined;
    }

    return limit;
  }

  setValidLimits(minTime, maxTime) {
    const min = minTime && this.inMinutes(minTime);
    const max = maxTime && this.inMinutes(maxTime);

    if (minTime && maxTime) {
      if (max <= min) {
        maxTime = undefined;
      }
    }

    if (minTime) {
      this.minTime = this.getValidLimit(minTime);
    }

    if (maxTime) {
      this.maxTime = this.getValidLimit(maxTime);
    }
  }

  getValidTime(time) {
    const timeInMinutes = this.inMinutes(time);

    if (this.minTime && timeInMinutes < this.inMinutes(this.minTime)) {
      return this.getViewValue(this.minTime);
    }

    if (this.maxTime && timeInMinutes > this.inMinutes(this.maxTime)) {
      return this.getViewValue(this.maxTime);
    }

    return this.getViewValue(time);
  }

  formatNumber(number) {
    return number.toString().length === 1 ? `0${number}` : number.toString();
  }

  setupTimeArrows(time) {
    this.incrementHoursDisabled = false;
    this.decrementHoursDisabled = false;
    this.incrementMinutesDisabled = false;
    this.decrementMinutesDisabled = false;
    this.meridianDisabled = false;

    const fullDayInMinutes = 1440;
    const halfOfDayInMinutes = fullDayInMinutes / 2;
    const hourStepInMinutes = this.hourStep * 60;
    const fullDayMinusCurrentTime = fullDayInMinutes - time;
    const timeToAfternoon = time - halfOfDayInMinutes;
    const timeToMidnight = time + halfOfDayInMinutes;

    if (this.minTime) {
      const minTime = this.inMinutes(this.minTime);
      const minTimePlusHourStep = minTime + hourStepInMinutes;
      const minTimePlusMinuteStep = minTime + this.minuteStep;
      const timeIsInNotAllowedRange = timeToAfternoon <= minTime - this.minuteStep;
      const timeIsLaterOrEqualToAfternoon = timeToAfternoon >= 0;

      if (time < minTimePlusHourStep) {
        this.decrementHoursDisabled = true;
      }

      if (time < minTimePlusMinuteStep) {
        this.decrementMinutesDisabled = true;
      }

      if (fullDayMinusCurrentTime <= hourStepInMinutes) {
        this.incrementHoursDisabled = true;
      }

      if (fullDayMinusCurrentTime <= this.minuteStep) {
        this.incrementMinutesDisabled = true;
      }

      if (timeIsInNotAllowedRange && timeIsLaterOrEqualToAfternoon) {
        this.meridianDisabled = true;
      }
    }

    if (this.maxTime) {
      const maxTime = this.inMinutes(this.maxTime);
      const maxTimeMinusHourStep = maxTime - hourStepInMinutes;
      const maxTimeMinusMinuteStep = maxTime - this.minuteStep;
      const timeIsInNotAllowedRange = timeToMidnight >= maxTime + this.minuteStep;
      const timeIsEarlierOrEqualToMidnight = timeToMidnight < fullDayInMinutes;

      if (time > maxTimeMinusHourStep) {
        this.incrementHoursDisabled = true;
      }

      if (time > maxTimeMinusMinuteStep) {
        this.incrementMinutesDisabled = true;
      }

      if (time < hourStepInMinutes) {
        this.decrementHoursDisabled = true;
      }

      if (time < this.minuteStep) {
        this.decrementMinutesDisabled = true;
      }

      if (timeIsInNotAllowedRange && timeIsEarlierOrEqualToMidnight) {
        this.meridianDisabled = true;
      }
    }
  }

  formatModel(modelValue) {
    if (modelValue != null) {
      const timeInMinutes = this.inMinutes(modelValue);

      this.setupTimeArrows(timeInMinutes);
      return this.getValidTime(modelValue);
    }

    return modelValue;
  }

  renderView() {
    this.time = angular.copy(this.ngModel.$viewValue);
  }

  parseView(viewValue) {
    const modelValue = {};

    modelValue.hours = this.hours.modelHours(viewValue.hours, viewValue.meridian);
    modelValue.minutes = parseInt(viewValue.minutes, 10);

    return modelValue;
  }

  incrementHours(bypassCheck) {
    if (!bypassCheck && (!this.on || this.incrementHoursDisabled)) {
      return;
    }

    this.time.hours = parseInt(this.time.hours, 10) + this.hourStep;
    [this.time.hours, this.time.meridian] =
      this.hours.filterIncremented(this.time.hours, this.time.meridian);

    this.updateHours();
  }

  decrementHours(bypassCheck) {
    if (!bypassCheck && (!this.on || this.decrementHoursDisabled)) {
      return;
    }

    this.time.hours = parseInt(this.time.hours, 10);
    const belowStep = this.hourStep > this.time.hours;
    this.time.hours = this.hours.adjustNegativeDecrement(this.time.hours, belowStep);

    this.time.hours = this.time.hours - this.hourStep;
    [this.time.hours, this.time.meridian] =
      this.hours.filterDecremented(this.time.hours, this.time.meridian);

    this.updateHours();
  }

  incrementMinutes() {
    if (!this.on || this.incrementMinutesDisabled) {
      return;
    }

    this.time.minutes = parseInt(this.time.minutes, 10) + this.minuteStep;
    if (this.time.minutes >= 60) {
      this.time.minutes = this.time.minutes - 60;
      this.incrementHours(true);
    }

    this.updateMinutes();
  }

  decrementMinutes() {
    if (!this.on || this.decrementMinutesDisabled) {
      return;
    }

    this.time.minutes = parseInt(this.time.minutes, 10) - this.minuteStep;
    if (this.time.minutes < 0) {
      this.time.minutes = this.time.minutes + 60;
      this.decrementHours(true);
    }

    this.updateMinutes();
  }

  updateHours() {
    let hours = parseInt(this.time.hours, 10);

    if (this.time.meridian) {
      if (!Number.isInteger(hours) || hours < 1 || hours > 12) {
        hours = 12;
      }
    } else if (!Number.isInteger(hours) || hours < 0 || hours > 23) {
      hours = 0;
    }
    this.time.hours = this.formatNumber(hours.toString());

    if (!this.viewHours || this.viewHours && this.viewHours !== this.time.hours) {
      this.updateTime();
      this.viewHours = undefined;
    }
  }

  updateMinutes() {
    let minutes = parseInt(this.time.minutes, 10);

    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
      minutes = 0;
    }

    this.time.minutes = this.formatNumber(minutes.toString());

    if (!this.viewMinutes || this.viewMinutes && this.viewMinutes !== this.time.minutes) {
      this.updateTime();
      this.viewMinutes = undefined;
    }
  }

  toggleMeridian() {
    this.time.meridian = this.time.meridian === 'AM' ? 'PM' : 'AM';
    this.updateTime();
  }

  initTime() {
    if (!this.time && this.on) {
      this.time = this.formatModel({
        hours: 0,
        minutes: 0
      });
      this.updateTime();
    }
  }

  inMinutes(timeModel) {
    return (timeModel.hours || 0) * 60 + (timeModel.minutes || 0);
  }

  updateTime() {
    const parsedTime = this.parseView(this.time);
    const timeInMinutes = this.inMinutes(parsedTime);

    this.setupTimeArrows(timeInMinutes);
    this.ngModel.$setViewValue(angular.copy(this.time));
  }

  selectOnFocus($event) {
    this.$timeout(() => {
      $event.target.setSelectionRange(0, $event.target.value.length);
    }, 0);
  }

  get timeDisplay() {
    return this.hours.getTimeDisplay(this.time);
  }

  get on() {
    return !this.isReadonly && !this.isDisabled;
  }

}

const timeComponent = {
  require: {
    ngModel: 'ngModel'
  },
  bindings: {
    placeholder: '@',
    hourStep: '<',
    minuteStep: '<',
    minTime: '<',
    maxTime: '<',
    isDisabled: '<',
    isReadonly: '<'
  },
  controller: TimeController,
  template($element, $attrs) {
    if (angular.isDefined($attrs.inline)) {
      const timeInlineElem = angular.element(timeInlineTemplate);
      timeInlineElem.addClass(INLINE_CLASS);
      return timeInlineElem[0].outerHTML;
    }
    const timeElem = angular.element(timeTemplate);
    const inlineWrapper = angular.element(timeElem[0].querySelector(INLINE_CONTAINER_SELECTOR));

    inlineWrapper.empty().append(angular.element(timeInlineTemplate));
    return timeElem[0].outerHTML;
  }
};

timeComponent.template.$inject = ['$element', '$attrs'];

export default timeComponent;
